import { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import SEOHead from '../../components/SEO/SEOHead';
import SEOFooter from '../../components/SEO/SEOFooter';
import { ArrowLeft, Search, ChevronRight } from 'lucide-react';

const CantosEscanteiosPage = () => {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const techniques = [
    {
      number: '01',
      title: 'Regra dos 37 Minutos',
      description: '65% dos jogos tem over 4.5 cantos no 1º tempo se houver 2+ até 37min',
      category: 'timing',
      accuracy: 65
    },
    {
      number: '02',
      title: 'Times de Pressão Alta',
      description: 'Equipes que pressionam alto tem média de 13+ cantos por jogo',
      category: 'tactical',
      accuracy: 78
    },
    {
      number: '03',
      title: 'Handicap Asiático',
      description: 'Mercado asiático de cantos paga 40% mais que tradicionais',
      category: 'market',
      accuracy: 72
    },
    {
      number: '04',
      title: 'Minutos Decisivos',
      description: '7-12, 35-40 e 78-85 minutos concentram 47% dos escanteios',
      category: 'timing',
      accuracy: 83
    },
    {
      number: '05',
      title: 'Fator Climático',
      description: 'Jogos com chuva tem média de +3.2 escanteios',
      category: 'external',
      accuracy: 69
    },
    {
      number: '06',
      title: 'Race to 3 Corners',
      description: 'Favorito chega primeiro a 3 cantos em 78% das vezes',
      category: 'market',
      accuracy: 78
    },
    {
      number: '07',
      title: 'Análise de Laterais',
      description: 'Laterais ofensivos geram 35% mais escanteios',
      category: 'tactical',
      accuracy: 71
    },
    {
      number: '08',
      title: 'Estratégia Múltipla',
      description: '3 jogos over 8.5 paga melhor que 1 jogo over 12.5',
      category: 'strategy',
      accuracy: 75
    },
    {
      number: '09',
      title: 'Impacto do VAR',
      description: 'VAR adiciona média de 1.8 cantos no tempo adicional',
      category: 'external',
      accuracy: 68
    },
    {
      number: '10',
      title: 'Clássicos Regionais',
      description: 'Derbies tem 28% mais cantos que jogos normais',
      category: 'tactical',
      accuracy: 82
    },
    {
      number: '11',
      title: 'Padrão 2º Tempo',
      description: 'Se 1º tempo <3 cantos, 89% chance de over 5.5 no 2º',
      category: 'timing',
      accuracy: 89
    },
    {
      number: '12',
      title: 'Perfil de Árbitros',
      description: 'Identificação de árbitros que geram mais escanteios',
      category: 'external',
      accuracy: 73
    },
    {
      number: '13',
      title: 'Pressão por Resultado',
      description: 'Time perdendo gera 4.7 cantos nos últimos 20 minutos',
      category: 'tactical',
      accuracy: 85
    },
    {
      number: '14',
      title: 'Dimensões do Campo',
      description: 'Campos menores tem média de +2.1 escanteios/jogo',
      category: 'external',
      accuracy: 67
    },
    {
      number: '15',
      title: 'Apostas ao Vivo',
      description: 'Odds live tem margem 50% menor que pré-jogo',
      category: 'market',
      accuracy: 91
    }
  ];

  const realExamples = [
    {
      match: 'Flamengo x Palmeiras',
      league: 'Brasileirão Série A',
      bet: 'Over 10.5 cantos',
      odd: 2.25,
      result: '14 cantos',
      profit: 125,
      date: '2024-10-15'
    },
    {
      match: 'Liverpool x Chelsea',
      league: 'Premier League',
      bet: 'Race to 5 corners',
      odd: 1.95,
      result: 'Liverpool venceu',
      profit: 95,
      date: '2024-10-08'
    },
    {
      match: 'River x Boca',
      league: 'Copa Libertadores',
      bet: '1º Tempo Over 4.5',
      odd: 2.70,
      result: '6 cantos no HT',
      profit: 170,
      date: '2024-09-29'
    },
    {
      match: 'Barcelona x Real Madrid',
      league: 'La Liga',
      bet: 'Total Exato 11-12',
      odd: 4.50,
      result: '11 cantos',
      profit: 350,
      date: '2024-09-21'
    },
    {
      match: 'Man City x Arsenal',
      league: 'Premier League',
      bet: 'Handicap -3.5 City',
      odd: 2.15,
      result: 'City 8 x 3 Arsenal',
      profit: 115,
      date: '2024-09-15'
    },
    {
      match: 'Bayern x Dortmund',
      league: 'Bundesliga',
      bet: 'Both Teams 4+ corners',
      odd: 1.85,
      result: 'Bayern 7, BVB 5',
      profit: 85,
      date: '2024-09-10'
    }
  ];

  const faqs = [
    {
      question: 'Por que apostar em cantos é mais lucrativo?',
      answer: 'As casas de apostas dedicam menos recursos para precificar cantos comparado a outros mercados. Enquanto gols têm 100+ variáveis analisadas, cantos têm apenas 20-30, criando oportunidades de valor consistentes.'
    },
    {
      question: 'Qual a média de cantos no futebol brasileiro?',
      answer: 'Brasileirão Série A tem média de 9.8 cantos/jogo, Série B 8.9, Copa do Brasil 10.4. Campeonatos estaduais variam de 7.5 a 11.2 dependendo da região e nível técnico.'
    },
    {
      question: 'Como a IA analisa cantos com precisão?',
      answer: 'Nossa IA processa 47 variáveis incluindo posse de bola esperada, cruzamentos/jogo, altura dos zagueiros, estilo tático, histórico do árbitro, condições climáticas e pressão por resultado.'
    },
    {
      question: 'Qual valor inicial recomendado?',
      answer: 'Recomendamos começar com R$ 100-200, usando stakes de 2-5% da banca por aposta para gestão segura e crescimento sustentável.'
    },
    {
      question: 'Vale a pena mercados asiáticos?',
      answer: 'Sim! Handicap asiático de cantos oferece proteção parcial e odds 30-40% melhores. Nossa IA é especializada nesses mercados complexos.'
    }
  ];

  const CATEGORY_LABELS = {
    all: 'Todas',
    timing: 'Timing',
    tactical: 'Tático',
    market: 'Mercado',
    external: 'Externo',
    strategy: 'Estratégia'
  };

  const filteredTechniques = techniques.filter(technique => {
    const matchesCategory = selectedCategory === 'all' || technique.category === selectedCategory;
    const matchesSearch = technique.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          technique.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const categoryCount = techniques.reduce((acc, technique) => {
    acc[technique.category] = (acc[technique.category] || 0) + 1;
    return acc;
  }, {});

  // Structured Data for SEO
  const articleStructuredData = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Guia Completo de Apostas em Cantos e Escanteios 2024",
    "description": "Aprenda 15 técnicas profissionais para lucrar com apostas em escanteios. Análise com IA, estratégias comprovadas e exemplos reais.",
    "author": {
      "@type": "Organization",
      "name": "PickStarAI"
    },
    "publisher": {
      "@type": "Organization",
      "name": "PickStarAI",
      "logo": {
        "@type": "ImageObject",
        "url": "https://pickstarai.com/logo.png"
      }
    },
    "datePublished": "2025-08-22",
    "dateModified": new Date().toISOString(),
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": "https://pickstarai.com/cantos-escanteios"
    }
  };

  const faqStructuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <>
      <SEOHead
        title="Apostas em Cantos e Escanteios 2024 | Guia Completo com IA"
        description="Descubra 15 técnicas profissionais para lucrar com apostas em escanteios. Análise com IA, estratégias comprovadas, exemplos reais e ROI de até 816%."
        keywords="apostas cantos, escanteios, corners betting, apostas escanteios, cantos asiáticos, over corners, estratégias cantos, apostas futebol cantos"
        canonical="https://pickstarai.com/cantos-escanteios"
        ogImage="https://pickstarai.com/og-cantos.jpg"
        structuredData={[articleStructuredData, faqStructuredData]}
      />

      <div className="min-h-screen bg-black">
        {/* Glass layer background */}
        <div className="absolute inset-0 bg-gradient-to-b from-white/[0.02] to-transparent" />

        {/* Terminal-style Header */}
        <header className="fixed top-0 z-50 w-full bg-black border-b border-white/[0.05]">
          <div className="max-w-screen-xl mx-auto px-6 py-3">
            <div className="flex items-center justify-between">
              <Link
                to="/"
                className="flex items-center text-[11px] text-white/50 hover:text-white/80 transition-all uppercase tracking-[0.15em]"
              >
                <ArrowLeft className="w-3 h-3 mr-2" />
                VOLTAR
              </Link>
              <div className="text-[9px] text-white/30 uppercase tracking-[0.2em]">
                ATUALIZADO: {new Date().toLocaleDateString('pt-BR').toUpperCase()}
              </div>
            </div>
          </div>
        </header>

        {/* Hero Section */}
        <section className="min-h-[60vh] flex items-center justify-center px-8 pt-20 pb-12">
          <div className="text-center max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="mb-6"
            >
              <h1 className="text-5xl lg:text-7xl font-thin text-white/95 leading-none mb-2">
                Cantos e Escanteios
              </h1>
              <p className="text-xs text-white/50 uppercase tracking-[0.2em] mt-4">
                15 técnicas profissionais para o mercado mais lucrativo do futebol
              </p>
            </motion.div>

            {/* Search Bar */}
            <div className="mt-8 max-w-md mx-auto">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white/30 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Buscar técnicas..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-11 pr-4 py-3 bg-white/[0.02] border border-white/[0.05] rounded text-sm text-white/80 placeholder-white/30 focus:outline-none focus:border-white/10 transition-all"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <div className="relative z-10 max-w-screen-xl mx-auto px-6 py-12">

          {/* Quick Stats */}
          <div className="grid grid-cols-4 gap-4 mb-16 pb-12 border-b border-white/[0.05]">
            <div className="text-center">
              <div className="text-3xl font-mono text-white/95">93%</div>
              <div className="text-[10px] text-white/30 uppercase tracking-wider mt-1">IGNORAM</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-mono text-emerald-400/80">47K</div>
              <div className="text-[10px] text-white/30 uppercase tracking-wider mt-1">LUCRO/MÊS</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-mono text-white/95">2.8x</div>
              <div className="text-[10px] text-white/30 uppercase tracking-wider mt-1">ODDS</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-mono text-emerald-400/80">PRO</div>
              <div className="text-[10px] text-white/30 uppercase tracking-wider mt-1">PRECISÃO</div>
            </div>
          </div>

          {/* Category Filter */}
          <div className="mb-12">
            <div className="flex gap-2 overflow-x-auto">
              {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
                <button
                  key={key}
                  onClick={() => setSelectedCategory(key)}
                  className={`px-4 py-2 text-xs font-medium uppercase tracking-wider transition-all whitespace-nowrap ${
                    selectedCategory === key
                      ? 'bg-emerald-500/20 text-emerald-400/80 border border-emerald-500/30'
                      : 'bg-white/[0.02] text-white/50 border border-white/[0.05] hover:border-white/10'
                  }`}
                >
                  {label} {key !== 'all' && `(${categoryCount[key] || 0})`}
                </button>
              ))}
            </div>
          </div>

          {/* Techniques Grid */}
          <section className="mb-16 pb-12 border-b border-white/[0.05]">
            <h2 className="text-2xl font-light text-white/95 mb-6">
              <span className="text-[11px] text-white/30 uppercase tracking-[0.3em] block mb-1">ESTRATÉGIAS</span>
              15 TÉCNICAS PROFISSIONAIS
            </h2>

            <div className="grid md:grid-cols-2 gap-4">
              {filteredTechniques.map((technique, index) => (
                <motion.div
                  key={technique.number}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white/[0.02] border border-white/[0.05] hover:border-white/10 p-6 transition-all"
                >
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 border border-emerald-500/20 flex items-center justify-center">
                        <span className="text-emerald-400/80 font-mono text-sm">{technique.number}</span>
                      </div>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-sm font-medium text-white/80 uppercase tracking-[0.15em] mb-2">
                        {technique.title}
                      </h3>
                      <p className="text-sm text-white/50 mb-3">{technique.description}</p>
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-white/40 uppercase tracking-wider">
                          {CATEGORY_LABELS[technique.category]}
                        </span>
                        <span className="font-mono text-emerald-400/80">{technique.accuracy}%</span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </section>

          {/* Real Examples */}
          <section className="mb-16 pb-12 border-b border-white/[0.05]">
            <h2 className="text-2xl font-light text-white/95 mb-6">
              <span className="text-[11px] text-white/30 uppercase tracking-[0.3em] block mb-1">RESULTADOS</span>
              EXEMPLOS REAIS DE LUCROS
            </h2>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
              {realExamples.map((example, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white/[0.02] border border-white/[0.05] hover:border-white/10 p-6 transition-all"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-sm font-medium text-white/90">{example.match}</h3>
                      <p className="text-[10px] text-white/40 uppercase tracking-wider">{example.league}</p>
                    </div>
                    <span className="text-xs font-mono text-emerald-400/80">
                      +{example.profit}%
                    </span>
                  </div>

                  <div className="space-y-2 text-sm border-t border-white/[0.05] pt-4">
                    <div className="flex justify-between">
                      <span className="text-white/40 text-xs uppercase tracking-wider">Aposta:</span>
                      <span className="text-white/70 text-xs">{example.bet}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-white/40 text-xs uppercase tracking-wider">Odd:</span>
                      <span className="font-mono text-white/80">@{example.odd}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-white/40 text-xs uppercase tracking-wider">Resultado:</span>
                      <span className="text-emerald-400/80 text-xs">{example.result}</span>
                    </div>
                  </div>

                  <div className="mt-4 pt-4 border-t border-white/[0.05]">
                    <div className="text-[10px] text-white/30 text-center uppercase tracking-wider">{example.date}</div>
                  </div>
                </motion.div>
              ))}
            </div>

            <div className="bg-white/[0.02] border border-white/[0.05] rounded p-6 text-center">
              <div className="text-3xl font-mono text-emerald-400/80 mb-2">
                ROI TOTAL: 816%
              </div>
              <p className="text-sm text-white/50">
                Lucro de R$ 2.450 com R$ 300 investidos seguindo análises da nossa IA
              </p>
            </div>
          </section>

          {/* FAQ Section */}
          <section className="mb-16">
            <h2 className="text-2xl font-light text-white/95 mb-6">
              <span className="text-[11px] text-white/30 uppercase tracking-[0.3em] block mb-1">FAQ</span>
              PERGUNTAS FREQUENTES
            </h2>

            <div className="space-y-0 border border-white/[0.05]">
              {faqs.map((faq, index) => (
                <details key={index} className="group">
                  <summary className="px-6 py-4 cursor-pointer flex justify-between items-center hover:bg-white/[0.02] transition-colors border-b border-white/[0.05] last:border-b-0">
                    <span className="text-sm text-white/80 font-medium">{faq.question}</span>
                    <ChevronRight className="w-4 h-4 text-white/40 group-open:rotate-90 transition-transform" />
                  </summary>
                  <div className="px-6 py-4 text-sm text-white/60 leading-relaxed border-b border-white/[0.05] last:border-b-0">
                    {faq.answer}
                  </div>
                </details>
              ))}
            </div>
          </section>

          {/* CTA Section */}
          <section className="text-center py-12 border-t border-white/[0.05]">
            <h2 className="text-2xl font-light text-white/95 mb-4">
              <span className="text-[11px] text-white/30 uppercase tracking-[0.3em] block mb-2">COMEÇAR</span>
              COMECE A LUCRAR COM CANTOS
            </h2>
            <p className="text-sm text-white/50 mb-8 max-w-2xl mx-auto">
              Faça parte dos 7% que lucram consistentemente com o mercado de escanteios
            </p>
            <button
              onClick={() => navigate('/chat')}
              className="inline-flex items-center justify-center px-10 py-4 bg-gradient-to-r from-emerald-500/80 to-violet-500/80
                       text-sm font-medium text-white uppercase tracking-[0.15em]
                       hover:from-emerald-500 hover:to-violet-500 transition-all"
            >
              ANÁLISE GRATUITA AGORA
              <ChevronRight className="ml-2 w-4 h-4" />
            </button>
          </section>
        </div>

        {/* Footer */}
        <div className="border-t border-white/[0.05]">
          <SEOFooter language="pt-BR" />
        </div>
      </div>
    </>
  );
};

export default CantosEscanteiosPage;
